/**
 * Version Checker Utility
 * Detects new deployments by comparing build versions.
 * Shows alert to users and reloads the app when a new version is available.
 */

export const AUTO_RELOAD_DELAY_MINUTES = 5;
export const DEFAULT_INTERVAL_MINUTES = 5;

const isDev = process.env.NODE_ENV === 'development';

class VersionChecker {
  constructor() {
    this.currentVersion = null;
    this.checkInterval = null;
    this.isChecking = false;
    this.isInitialized = false;
    this.alreadyNotified = false;
    this.autoReloadTimer = null;
    this.intervalMinutes = DEFAULT_INTERVAL_MINUTES;
    this.intervalTime = this.intervalMinutes * 60 * 1000;
    this.autoReloadDelay = AUTO_RELOAD_DELAY_MINUTES * 60 * 1000;

    // Bind handlers so they can be cleanly removed in destroy()
    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    this._onOnline = this._onOnline.bind(this);
  }

  /**
   * Initialize version checker.
   * Calling this more than once is a no-op — call destroy() first to reset.
   * @param {number} intervalMinutes - Poll interval in minutes (default: 5)
   */
  async init(intervalMinutes = DEFAULT_INTERVAL_MINUTES) {
    if (this.isInitialized) return;
    this.isInitialized = true;

    this.intervalMinutes = intervalMinutes;
    this.intervalTime = intervalMinutes * 60 * 1000;

    try {
      this.currentVersion = await this.fetchVersion();
    } catch (error) {
      if (isDev) console.error('Failed to fetch initial version:', error);
    }

    this.startChecking();
    this._registerEventListeners();
  }

  // ── Event listeners ────────────────────────────────────────────────────────

  /** Pause polling when the tab is hidden; resume and check immediately when visible. */
  _onVisibilityChange() {
    if (document.visibilityState === 'visible') {
      this.startChecking();
      this.checkForUpdates();
    } else {
      this.stopChecking();
    }
  }

  /** When network connectivity is restored, check immediately and restart polling. */
  _onOnline() {
    this.startChecking();
    this.checkForUpdates();
  }

  _registerEventListeners() {
    document.addEventListener('visibilitychange', this._onVisibilityChange);
    window.addEventListener('online', this._onOnline);
  }

  _removeEventListeners() {
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    window.removeEventListener('online', this._onOnline);
  }

  // ── Core logic ─────────────────────────────────────────────────────────────

  /**
   * Fetch the version field from meta.json (cache-busted).
   * @returns {Promise<string|null>} version string, or null on any failure
   */
  async fetchVersion() {
    try {
      const response = await fetch(`/meta.json?t=${Date.now()}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache',
          'Pragma': 'no-cache',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();
      return data.version ?? null;
    } catch (error) {
      if (isDev) console.error('Error fetching version:', error);
      return null;
    }
  }

  /**
   * Compare the latest deployed version against the one loaded in this session.
   * Notifies the user if a newer version is detected.
   */
  async checkForUpdates() {
    if (this.isChecking || this.alreadyNotified) return;
    if (!navigator.onLine) return;

    this.isChecking = true;

    try {
      const latestVersion = await this.fetchVersion();

      if (!latestVersion) return;

      if (this.currentVersion && latestVersion !== this.currentVersion) {
        this.notifyUpdate();
      }

      this.currentVersion = latestVersion;
    } catch (error) {
      if (isDev) console.error('Error checking for updates:', error);
    } finally {
      this.isChecking = false;
    }
  }

  /**
   * Dispatch the update alert and schedule the auto-reload.
   * Fires at most once; polling stops immediately.
   */
  notifyUpdate() {
    this.alreadyNotified = true;
    this.stopChecking();

    this.autoReloadTimer = setTimeout(() => {
      this.reloadApp();
    }, this.autoReloadDelay);

    window.dispatchEvent(
      new CustomEvent('showAlert', {
        detail: {
          type: 'important',
          title: `A new version of the application is available. The app will auto-reload in ${AUTO_RELOAD_DELAY_MINUTES} minutes. Click "Update Now" to reload immediately.`,
          confirmBtnText: 'UPDATE NOW',
          cancelBtnText: `Auto-Update in ${AUTO_RELOAD_DELAY_MINUTES} minutes`,
          showCloseBtn: true,
          size: 'lg',
          dialogClassName: 'version-update-alert',
          onConfirm: () => {
            if (this.autoReloadTimer) {
              clearTimeout(this.autoReloadTimer);
              this.autoReloadTimer = null;
            }
            this.reloadApp();
          },
          onCancel: () => {},
          onClose: () => {},
        },
      })
    );
  }

  /**
   * Clear browser caches and unregister service workers, then hard-reload.
   * Both cleanup steps are awaited so caches are empty before the new page loads.
   */
  async reloadApp() {
    const tasks = [];

    if ('caches' in window) {
      tasks.push(
        caches.keys().then((names) =>
          Promise.all(names.map((name) => caches.delete(name)))
        )
      );
    }

    if ('serviceWorker' in navigator) {
      tasks.push(
        navigator.serviceWorker
          .getRegistrations()
          .then((registrations) =>
            Promise.all(registrations.map((r) => r.unregister()))
          )
      );
    }

    try {
      await Promise.all(tasks);
    } catch {
      // Proceed with reload even if cleanup fails
    }

    window.location.reload();
  }

  // ── Interval management ────────────────────────────────────────────────────

  /** Start (or restart) the polling interval. No-op once a notification has been sent. */
  startChecking() {
    if (this.alreadyNotified) return;

    if (this.checkInterval) {
      clearInterval(this.checkInterval);
    }

    this.checkInterval = setInterval(() => {
      this.checkForUpdates();
    }, this.intervalTime);
  }

  /** Stop the polling interval. */
  stopChecking() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /** Immediately trigger a version check outside the normal interval. */
  checkNow() {
    this.checkForUpdates();
  }

  /**
   * Tear down the checker fully.
   * Safe to call init() again afterwards.
   */
  destroy() {
    this.stopChecking();
    this._removeEventListeners();

    if (this.autoReloadTimer) {
      clearTimeout(this.autoReloadTimer);
      this.autoReloadTimer = null;
    }

    this.currentVersion = null;
    this.isInitialized = false;
    this.alreadyNotified = false;
  }
}

// Singleton instance shared across the app
const versionChecker = new VersionChecker();

export default versionChecker;
